// src/pages/Trend/components/analysis/ingredient-analysis/CategoryAnalysisChart.js
import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import ChartContainer from '../common/ChartContainer';
import './CategoryAnalysisChart.css';

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

// Sophisticated Cosmetic Palette
const PALETTE = [
  '#2E93fA', '#66DA26', '#546E7A', '#E91E63', '#FF9800',
  '#9C27B0', '#00BCD4', '#FF5722', '#795548', '#607D8B'
];

// Helper to generate a lighter version of a color for gradient
const getLighterColor = (color) => {
  const c = d3.color(color);
  return c ? c.brighter(1.5).formatHex() : color;
};

// Helper to generate a slightly darker version for gradient edge
const getDarkerColor = (color) => {
  const c = d3.color(color);
  return c ? c.darker(0.3).formatHex() : color;
};

/**
 * Category Analysis Chart - Premium Glass & Sphere Design
 */
const CategoryAnalysisChart = ({ data }) => {
  const { translate } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeSet, setActiveSet] = useState('primary');
  const svgRef = useRef();
  const containerRef = useRef();

  const hasSecondary = !!data?.secondary?.categorized_ingredients;
  const primary = data?.primary;
  const secondary = data?.secondary;

  // Process category data into hierarchical format for D3
  const processedData = useMemo(() => {
    if (!primary?.categorized_ingredients) return null;

    const processCategories = (categories) => {
      const entries = Object.entries(categories)
        .filter(([_, values]) => values.total > 0)
        .sort((a, b) => b[1].total - a[1].total);

      const total = entries.reduce((sum, [_, cat]) => sum + cat.total, 0);

      return entries.map(([name, values], index) => ({
        name,
        value: values.total,
        percentage: ((values.total / total) * 100).toFixed(1),
        color: name === 'other' ? '#999999' : PALETTE[index % PALETTE.length],
        items: values.items
      }));
    };

    const primaryCategories = processCategories(primary.categorized_ingredients);
    const secondaryCategories = hasSecondary
      ? processCategories(secondary.categorized_ingredients)
      : null;

    // Create unified color map
    const colorMap = {};
    primaryCategories.forEach((cat) => { colorMap[cat.name] = cat.color; });

    // Sync secondary colors
    if (secondaryCategories) {
      secondaryCategories.forEach(cat => {
        if (colorMap[cat.name]) cat.color = colorMap[cat.name];
        else colorMap[cat.name] = cat.color;
      });
    }

    const toHierarchy = (categories) => ({
      name: 'root',
      children: categories.map(cat => ({
        ...cat,
        children: cat.items?.labels?.slice(0, 10).map((label, idx) => ({
          name: label,
          value: cat.items.values[idx] || 1,
          parentColor: cat.color
        })) || []
      }))
    });

    return {
      primary: toHierarchy(primaryCategories),
      secondary: secondaryCategories ? toHierarchy(secondaryCategories) : null,
      colorMap,
      primaryFlat: primaryCategories,
      secondaryFlat: secondaryCategories
    };
  }, [primary, secondary, hasSecondary]);

  // D3 Circle Pack Rendering with Spherical Gradients
  useEffect(() => {
    if (!processedData || !svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth || 600;
    const height = container.clientHeight || 480;
    const currentData = activeSet === 'primary' ? processedData.primary : processedData.secondary;

    if (!currentData) return;

    const root = d3.hierarchy(currentData)
      .sum(d => d.value)
      .sort((a, b) => b.value - a.value);

    const pack = d3.pack()
      .size([width, height])
      .padding(10);

    const packedRoot = pack(root);
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg.attr('viewBox', `0 0 ${width} ${height}`);

    // --- Definitions (Gradients & Filters) ---
    const defs = svg.append('defs');

    // Create Radial Gradients for Spherical Look with better contrast
    Object.entries(processedData.colorMap).forEach(([name, color]) => {
      const gradId = `grad-${name.replace(/[^a-zA-Z0-9]/g, '')}`;
      const grad = defs.append('radialGradient')
        .attr('id', gradId)
        .attr('cx', '35%')
        .attr('cy', '35%')
        .attr('r', '65%');

      grad.append('stop').attr('offset', '0%').attr('stop-color', getLighterColor(color));
      grad.append('stop').attr('offset', '70%').attr('stop-color', color);
      grad.append('stop').attr('offset', '100%').attr('stop-color', getDarkerColor(color));
    });

    // Drop Shadow for depth
    const shadow = defs.append('filter')
      .attr('id', 'dropShadow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    shadow.append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '4')
      .attr('stdDeviation', '6')
      .attr('flood-color', 'rgba(0,0,0,0.1)');

    // --- Drawing ---
    const g = svg.append('g');

    const categoryNodes = packedRoot.descendants().filter(d => d.depth === 1);
    const leafNodes = packedRoot.descendants().filter(d => d.depth === 2);

    // Draw Categories (The Spheres)
    const categoryGroups = g.selectAll('.category-group')
      .data(categoryNodes)
      .join('g')
      .attr('class', 'category-group')
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      .style('cursor', 'pointer');

    // The Bubble with gradient fill - increased opacity for readability
    categoryGroups.append('circle')
      .attr('r', d => d.r)
      .style('fill', d => {
        const safeName = d.data.name.replace(/[^a-zA-Z0-9]/g, '');
        return `url(#grad-${safeName})`;
      })
      .style('fill-opacity', 0.25)
      .style('stroke', d => d.data.color)
      .style('stroke-width', 2)
      .style('stroke-opacity', 0.6)
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelectedCategory(prev => prev === d.data.name ? null : d.data.name);
      })
      .on('mouseenter', function() {
        d3.select(this)
          .transition()
          .duration(300)
          .style('fill-opacity', 0.4)
          .style('stroke-opacity', 1);
      })
      .on('mouseleave', function(_, d) {
        const isSelected = selectedCategory === d.data.name;
        d3.select(this)
          .transition()
          .duration(300)
          .style('fill-opacity', isSelected ? 0.4 : 0.25)
          .style('stroke-opacity', isSelected ? 1 : 0.6);
      });

    // Draw Leaves (Ingredients) inside - subtle white circles
    const leafGroups = g.selectAll('.leaf-group')
      .data(leafNodes)
      .join('g')
      .attr('transform', d => `translate(${d.x}, ${d.y})`)
      .style('pointer-events', 'none');

    leafGroups.append('circle')
      .attr('r', d => d.r)
      .style('fill', '#fff')
      .style('fill-opacity', 0.5)
      .style('stroke', d => d.data.parentColor)
      .style('stroke-width', 0.5)
      .style('stroke-opacity', 0.3);

    // === LABELS GROUP (rendered last to be on top) ===
    const labelsGroup = svg.append('g').attr('class', 'labels-layer');

    // Category name labels with background for readability
    categoryNodes.filter(d => d.r > 30).forEach(d => {
      const labelGroup = labelsGroup.append('g')
        .attr('transform', `translate(${d.x}, ${d.y})`);

      const labelY = d.children && d.children.length > 0 ? -d.r + 20 : 0;

      // Background pill for text
      labelGroup.append('rect')
        .attr('x', -d.data.name.length * 3.5 - 6)
        .attr('y', labelY - 10)
        .attr('width', d.data.name.length * 7 + 12)
        .attr('height', 18)
        .attr('rx', 9)
        .style('fill', 'rgba(255,255,255,0.9)');

      // Category name text
      labelGroup.append('text')
        .attr('dy', labelY)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .text(d.data.name)
        .style('font-family', "'Inter', -apple-system, sans-serif")
        .style('font-size', Math.min(13, d.r / 3) + 'px')
        .style('font-weight', '700')
        .style('fill', d.data.color)
        .style('pointer-events', 'none');
    });

    // Percentage labels with background
    categoryNodes.filter(d => d.r > 50).forEach(d => {
      const labelGroup = labelsGroup.append('g')
        .attr('transform', `translate(${d.x}, ${d.y})`);

      const percentY = d.children && d.children.length > 0 ? -d.r + 38 : 18;
      const percentText = `${d.data.percentage}%`;

      // Background pill
      labelGroup.append('rect')
        .attr('x', -percentText.length * 3.5 - 4)
        .attr('y', percentY - 8)
        .attr('width', percentText.length * 7 + 8)
        .attr('height', 16)
        .attr('rx', 8)
        .style('fill', 'rgba(255,255,255,0.85)');

      // Percentage text
      labelGroup.append('text')
        .attr('dy', percentY)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .text(percentText)
        .style('font-family', "'Inter', -apple-system, sans-serif")
        .style('font-size', '11px')
        .style('font-weight', '600')
        .style('fill', '#444')
        .style('pointer-events', 'none');
    });

    // Visual State Update for Selection
    if (selectedCategory) {
      svg.selectAll('.category-group circle')
        .transition()
        .duration(400)
        .style('opacity', d => d.data.name === selectedCategory ? 1 : 0.25)
        .style('stroke-width', d => d.data.name === selectedCategory ? 3 : 1);

      // Dim labels of non-selected categories
      svg.selectAll('.labels-layer g')
        .transition()
        .duration(400)
        .style('opacity', function() {
          const transform = d3.select(this).attr('transform');
          const node = categoryNodes.find(n =>
            transform === `translate(${n.x}, ${n.y})`
          );
          return node && node.data.name === selectedCategory ? 1 : 0.3;
        });
    } else {
      svg.selectAll('.category-group circle')
        .transition()
        .duration(400)
        .style('opacity', 1)
        .style('stroke-width', 2);

      svg.selectAll('.labels-layer g')
        .transition()
        .duration(400)
        .style('opacity', 1);
    }

  }, [processedData, activeSet, selectedCategory]);

  if (!processedData) {
    return (
      <div className="category-analysis-error">
        {translate('trendPage.ingredientAnalysis.errors.noCategoryData')}
      </div>
    );
  }

  const topCategory = processedData.primaryFlat[0];
  const topSecondaryCategory = hasSecondary ? processedData.secondaryFlat?.[0] : null;

  // Helpers for Details
  const getSelectedDetails = (flatData) => flatData?.find(c => c.name === selectedCategory);
  const primaryDetails = getSelectedDetails(processedData.primaryFlat);
  const secondaryDetails = hasSecondary ? getSelectedDetails(processedData.secondaryFlat) : null;

  // Render ingredient list with staggered animation
  const renderIngredientList = (details, isPrimary = true) => {
    if (!details?.items?.labels) return null;

    const sorted = details.items.labels
      .map((l, i) => ({ label: l, value: details.items.values[i] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const max = sorted[0]?.value || 1;
    const barColor = isPrimary
      ? (processedData.colorMap[details.name] || BBC_STYLE.PRIMARY_COLOR)
      : BBC_STYLE.SECONDARY_COLOR;

    return (
      <div className="ingredient-list">
        {sorted.map((item, i) => (
          <div
            key={item.label}
            className="ingredient-item"
            style={{ animationDelay: `${i * 0.05}s` }}
          >
            <div className="ingredient-info">
              <span className="ingredient-name">{item.label}</span>
              <span className="ingredient-value">{item.value}</span>
            </div>
            <div className="ingredient-bar-bg">
              <div
                className="ingredient-bar"
                style={{
                  width: `${(item.value / max) * 100}%`,
                  background: barColor
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ChartContainer autoHeight={true}>
      <div className="category-analysis-chart">
        {/* Narrative Section */}
        <div className="category-narrative">
          {!hasSecondary ? (
            // Single mode
            <>
              <h2 className="narrative-title">
                {translate('trendPage.ingredientAnalysis.narratives.dominatedBy')}{' '}
                <span
                  className="highlight-ingredient"
                  style={{
                    color: topCategory.color,
                    borderBottom: `2px solid ${topCategory.color}`
                  }}
                >
                  {topCategory.name}
                </span>
              </h2>
              <div className="narrative-subtitle">
                <span className="stat-pill" style={{ background: `${topCategory.color}15` }}>
                  {processedData.primaryFlat.length} {translate('trendPage.ingredientAnalysis.units.categories')}
                </span>
                <span className="stat-divider">•</span>
                <span>{topCategory.percentage}% {translate('trendPage.ingredientAnalysis.category.ofTotal')}</span>
              </div>
            </>
          ) : (
            // Comparison mode
            <>
              <h2 className="narrative-title">
                {translate('trendPage.ingredientAnalysis.narratives.filterComparison')}
              </h2>
              <div className="narrative-comparison">
                <div className="comparison-item">
                  <div className="comparison-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
                  <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set1'))}:</span>
                  <span
                    className="highlight-ingredient"
                    style={{
                      color: topCategory.color,
                      borderBottom: `2px solid ${topCategory.color}`
                    }}
                  >
                    {topCategory.name}
                  </span>
                  <span className="comparison-category">({topCategory.percentage}%)</span>
                </div>
                <div className="comparison-item">
                  <div className="comparison-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
                  <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set2'))}:</span>
                  <span
                    className="highlight-ingredient"
                    style={{
                      color: topSecondaryCategory?.color,
                      borderBottom: `2px solid ${topSecondaryCategory?.color}`
                    }}
                  >
                    {topSecondaryCategory?.name}
                  </span>
                  <span className="comparison-category">({topSecondaryCategory?.percentage}%)</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Set Toggle for Comparison Mode */}
        {hasSecondary && (
          <div className="set-toggle">
            <button
              className={`set-toggle-btn ${activeSet === 'primary' ? 'active' : ''}`}
              onClick={() => setActiveSet('primary')}
            >
              <span className="toggle-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
              {renderFilterLabel(translate('trendPage.filters.set1'))}
            </button>
            <button
              className={`set-toggle-btn ${activeSet === 'secondary' ? 'active' : ''}`}
              onClick={() => setActiveSet('secondary')}
            >
              <span className="toggle-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
              {renderFilterLabel(translate('trendPage.filters.set2'))}
            </button>
          </div>
        )}

        {/* Main Content: Galaxy + Detail Panel */}
        <div className="galaxy-layout">
          <div className="galaxy-container" ref={containerRef}>
            <svg ref={svgRef} className="galaxy-svg" />
            {!selectedCategory && (
              <div className="hint-overlay">
                {translate('trendPage.ingredientAnalysis.category.selectCategory')}
              </div>
            )}
          </div>

          <div className={`detail-panel ${selectedCategory ? 'visible' : ''}`}>
            {selectedCategory ? (
              <>
                <div className="detail-header">
                  <span
                    className="detail-category-badge"
                    style={{ background: processedData.colorMap[selectedCategory] }}
                  >
                    {selectedCategory}
                  </span>
                  <button
                    className="detail-close"
                    onClick={() => setSelectedCategory(null)}
                  >
                    ×
                  </button>
                </div>

                <div className="detail-content">
                  {hasSecondary ? (
                    <div className="detail-comparison">
                      <div className="detail-column">
                        <div className="column-header">
                          <span className="column-set-label">{renderFilterLabel(translate('trendPage.filters.set1'))}</span>
                          <span
                            className="column-percent"
                            style={{ color: processedData.colorMap[selectedCategory] }}
                          >
                            {primaryDetails?.percentage || 0}%
                          </span>
                        </div>
                        {primaryDetails ? renderIngredientList(primaryDetails, true) : (
                          <div className="no-data">{translate('trendPage.ingredientAnalysis.category.noIngredients')}</div>
                        )}
                      </div>
                      <div className="detail-column">
                        <div className="column-header">
                          <span className="column-set-label">{renderFilterLabel(translate('trendPage.filters.set2'))}</span>
                          <span
                            className="column-percent"
                            style={{ color: BBC_STYLE.SECONDARY_COLOR }}
                          >
                            {secondaryDetails?.percentage || 0}%
                          </span>
                        </div>
                        {secondaryDetails ? renderIngredientList(secondaryDetails, false) : (
                          <div className="no-data">{translate('trendPage.ingredientAnalysis.category.noIngredients')}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="detail-metric-large">{primaryDetails?.percentage}%</div>
                      <span className="detail-metric-label">
                        {translate('trendPage.ingredientAnalysis.category.ofTotal')}
                      </span>
                      {renderIngredientList(primaryDetails, true)}
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="detail-placeholder">
                <div className="placeholder-icon">⦾</div>
                <div className="placeholder-text">
                  {translate('trendPage.ingredientAnalysis.category.selectCategory')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ChartContainer>
  );
};

CategoryAnalysisChart.propTypes = {
  data: PropTypes.shape({
    primary: PropTypes.shape({
      categorized_ingredients: PropTypes.object
    }),
    secondary: PropTypes.shape({
      categorized_ingredients: PropTypes.object
    })
  })
};

export default CategoryAnalysisChart;
